import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Card, Input, Modal, Select, Space, Tag, Typography, message } from "antd";
import type { DeviceVo, Page } from "@zhihaoscm/entity";
import ExcelJS from "exceljs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { shipService } from "@/api/services/shipService";
import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { ABLE, SORT } from "@/meta/common";
import { DEVICE_TYPE, ONLINE } from "@/meta/logistics/device";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import ShipMonitorModal, {
    type ShipMonitorDevice,
    type ShipMonitorMapShip,
    type ShipMonitorStream
} from "@/views/pages/logistics/shipManagement/ShipMonitorModal";

export interface DeviceManagementQuery {
    page: number;
    size: number;
    name?: string;
    online?: number;
    state?: number;
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

/** 将旧 Vue 设备列表的创建时间排序转换为分页接口参数。 */
export function buildDeviceManagementSortQuery(
    query: DeviceManagementQuery,
    sort: { prop?: string; order: "ascending" | "descending" | null }
): DeviceManagementQuery {
    const nextQuery = { ...query, page: 1 };
    delete nextQuery.sortKey;
    delete nextQuery.sortOrder;
    if (sort.prop === "device.createdTime" && sort.order) {
        nextQuery.sortKey = "created_time";
        nextQuery.sortOrder = SORT.getIntroByCode(sort.order);
    }
    return nextQuery;
}

export interface DeviceManagementPageProps {
    loadDevices?: (query: DeviceManagementQuery) => Promise<Page<DeviceVo>>;
    onlineDevice?: (id: number) => Promise<unknown>;
    toggleDeviceState?: (id: number, status: number) => Promise<unknown>;
    deleteDevice?: (id: number) => Promise<unknown>;
    loadMonitorShip?: (id: string) => Promise<Partial<ShipMonitorMapShip>>;
    loadMonitorStream?: (device: ShipMonitorDevice) => Promise<ShipMonitorStream | undefined>;
    loadShipLocation?: (mmsi: string) => Promise<ShipMonitorMapShip | undefined>;
}

/** 复用 Vue 设备管理分页接口。 */
export async function loadDeviceManagement(query: DeviceManagementQuery) {
    const response = await shippingRequestHttp.service.get<Page<DeviceVo>>("/device/paging", { params: query });
    return response.data;
}

/** 复用 Vue 设备上线接口。 */
export async function onlineDeviceManagement(id: number) {
    const response = await shippingRequestHttp.service.put(`/device/online/${id}`);
    return response.data;
}

/** 复用 Vue 设备启用/禁用接口。 */
export async function toggleDeviceManagementState(id: number, status: number) {
    const response = await shippingRequestHttp.service.put(`/device/update/${id}/state/${status}`);
    return response.data;
}

/** 复用 Vue 设备删除接口，实际删除前由列表弹窗完成序列号二次校验。 */
export async function deleteDeviceManagement(id: number) {
    const response = await shippingRequestHttp.service.delete(`/device/${id}`);
    return response.data;
}

/** 复用旧 Vue onVideo 的船舶补全接口，监控弹窗仅在列表缺少坐标时调用。 */
export async function loadDeviceManagementMonitorShip(id: string) {
    return (await shipService.findCreate(id)) as unknown as Partial<ShipMonitorMapShip>;
}

/** 沿用 Vue 设备列表导出的列顺序，生成浏览器下载文件。 */
async function downloadDeviceList(rows: DeviceVo[]) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("设备列表");
    const headers = [
        "名称",
        "类型",
        "序列号",
        "ICCID",
        "安装位置",
        "关联船舶",
        "负责人",
        "联系方式",
        "是否在线",
        "启用状态",
        "推介人",
        "安装人",
        "安装日期",
        "安装地点",
        "添加人",
        "添加时间"
    ];
    worksheet.addRow(headers);
    rows.forEach(row => {
        worksheet.addRow([
            row.device.name || "",
            formatEnum(DEVICE_TYPE, row.device.type),
            row.device.serialNo || "",
            row.device.dataCard || "",
            row.device.position || "",
            row.ship?.cnname || row.ship?.name || "",
            row.ship?.captain || "",
            row.ship?.mobile || "",
            formatEnum(ONLINE, row.device.online),
            formatEnum(ABLE, row.device.state),
            row.device.referrer || "",
            row.device.installer || "",
            row.device.installDate || "",
            row.device.installSite || "",
            row.user?.name || "",
            formatDate(row.device.createdTime)
        ]);
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const link = document.createElement("a");
    link.download = "设备列表.xlsx";
    link.href = window.URL.createObjectURL(new Blob([buffer]));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatEnum = (meta: { getIntroByCode: (code?: number) => string }, value?: number) =>
    meta.getIntroByCode(value) || DEFAULT_VALUE;

/** 物流管理-设备管理列表，覆盖 Vue 首屏字段、筛选和分页。 */
export function DeviceManagementPage({
    loadDevices = loadDeviceManagement,
    onlineDevice = onlineDeviceManagement,
    toggleDeviceState = toggleDeviceManagementState,
    deleteDevice = deleteDeviceManagement,
    loadMonitorShip = loadDeviceManagementMonitorShip,
    loadMonitorStream,
    loadShipLocation
}: DeviceManagementPageProps) {
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const canWrite = permissions.includes("ROLE_DEVICE_W");
    const canMonitor = permissions.includes("ROLE_DEVICE_R");
    const canExport = permissions.includes("ROLE_DEVICE_EXPORT");
    const [rows, setRows] = useState<DeviceVo[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<DeviceManagementQuery>({ page: 1, size: 20 });
    const [name, setName] = useState("");
    const [online, setOnline] = useState("");
    const [state, setState] = useState("");
    const [installDateRange, setInstallDateRange] = useState<[string, string] | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [pendingAction, setPendingAction] = useState<{
        type: "online" | "state";
        id: number;
        nextState?: number;
    } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<DeviceVo | null>(null);
    const [deleteSerial, setDeleteSerial] = useState("");
    const [deleteValidationError, setDeleteValidationError] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    const [monitorOpen, setMonitorOpen] = useState(false);
    const [monitorShip, setMonitorShip] = useState<Partial<ShipMonitorMapShip>>();
    const [monitorDevice, setMonitorDevice] = useState<ShipMonitorDevice>();
    const [monitorShipId, setMonitorShipId] = useState<string>();

    /** 先按当前筛选条件扩大分页查询，再由用户确认生成 Excel。 */
    const handleExport = async () => {
        try {
            const result = await loadDevices({ ...query, size: 1000 });
            if (!result.list?.length) {
                message.error("无数据导出");
                return;
            }
            Modal.confirm({
                title: "导出",
                content: "是否导出此筛选条件的设备信息？",
                okText: "确认",
                cancelText: "取消",
                onOk: () => downloadDeviceList(result.list || [])
            });
        } catch {
            setErrorMessage("设备导出失败");
        }
    };

    useEffect(() => {
        let disposed = false;
        void loadDevices(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("设备列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDevices, query]);

    const submitFilters = () => {
        const nextQuery: DeviceManagementQuery = { page: 1, size: 20 };
        if (name.trim()) nextQuery.name = name.trim();
        if (online !== "") nextQuery.online = Number(online);
        if (state !== "") nextQuery.state = Number(state);
        if (installDateRange?.[0]) nextQuery.beginTime = dateFormat(installDateRange[0], LOG_DATE_FORMAT_START);
        if (installDateRange?.[1]) nextQuery.endTime = dateFormat(installDateRange[1], LOG_DATE_FORMAT_END);
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setName("");
        setOnline("");
        setState("");
        setInstallDateRange(null);
        setQuery({ page: 1, size: 20 });
    };

    /** 旧 Vue sort-change 触发后回到第一页并保留当前筛选条件。 */
    const handleSortChange = useCallback((sort: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => buildDeviceManagementSortQuery(current, sort));
    }, []);

    const confirmOnline = useCallback((id: number) => {
        setPendingAction({ type: "online", id });
    }, []);

    const confirmToggleState = useCallback((id: number, currentState: number) => {
        const nextState = currentState === ABLE.ENABLE.code ? ABLE.DISABLE.code : ABLE.ENABLE.code;
        setPendingAction({ type: "state", id, nextState });
    }, []);

    /** 执行设备操作并刷新当前分页，避免确认框关闭后列表停留在旧状态。 */
    const handleActionConfirm = async () => {
        if (!pendingAction) return;
        setActionLoading(true);
        try {
            if (pendingAction.type === "online") {
                await onlineDevice(pendingAction.id);
                message.success("已上线");
            } else {
                const nextState = pendingAction.nextState ?? ABLE.ENABLE.code;
                await toggleDeviceState(pendingAction.id, nextState);
                message.success(nextState === ABLE.ENABLE.code ? "已启用" : "已禁用");
            }
            setPendingAction(null);
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage(pendingAction.type === "online" ? "设备上线失败" : "设备状态更新失败");
        } finally {
            setActionLoading(false);
        }
    };

    /** 按 Vue 规则先核验序列号，并禁止删除已绑定船舶的设备。 */
    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        if (deleteTarget.ship?.id) {
            setDeleteTarget(null);
            setErrorMessage("该设备已绑定船舶，请先解绑船舶！");
            return;
        }
        if (deleteSerial !== deleteTarget.device.serialNo) {
            setDeleteValidationError("序列号校验失败");
            return;
        }
        setActionLoading(true);
        try {
            await deleteDevice(deleteTarget.device.id);
            message.success("删除成功");
            setDeleteTarget(null);
            setDeleteSerial("");
            setDeleteValidationError("");
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage("设备删除失败");
        } finally {
            setActionLoading(false);
        }
    };

    /** 对齐旧 Vue onVideo：补全缺失的船舶坐标后，以当前列表设备打开监控弹窗。 */
    const handleMonitor = useCallback(
        async (row: DeviceVo) => {
            const shipId = row.ship?.id;
            if (!shipId) {
                setErrorMessage("监控加载失败");
                return;
            }
            const listShip = row.ship as unknown as Partial<ShipMonitorMapShip>;
            try {
                const hasCoordinates = Boolean(listShip.lon && listShip.lat);
                const resolvedShip = hasCoordinates
                    ? listShip
                    : { ...listShip, ...(await loadMonitorShip(String(shipId))) };
                setMonitorShip(resolvedShip);
                setMonitorDevice(row.device as unknown as ShipMonitorDevice);
                setMonitorShipId(String(shipId));
                setMonitorOpen(true);
                setErrorMessage("");
            } catch {
                setErrorMessage("监控加载失败");
            }
        },
        [loadMonitorShip]
    );

    const deviceOperation = useCallback(
        (row: DeviceVo) => (
            <Space>
                {canWrite && row.device.online !== ONLINE.ONLINE.code ? (
                    <Button disabled={banButton} type="link" onClick={() => confirmOnline(row.device.id)}>
                        上线
                    </Button>
                ) : null}
                {canMonitor ? (
                    <Button type="link" onClick={() => void handleMonitor(row)}>
                        监控
                    </Button>
                ) : null}
                {canWrite ? (
                    <Link
                        aria-label={`编辑设备${row.device.id}`}
                        to={`/logistics/device/deviceManagement/edit/${row.device.id}`}
                    >
                        <Button disabled={banButton} type="link">
                            编辑
                        </Button>
                    </Link>
                ) : null}
                {canWrite ? (
                    <Button
                        disabled={banButton}
                        type="link"
                        onClick={() => confirmToggleState(row.device.id, row.device.state)}
                    >
                        {row.device.state === ABLE.ENABLE.code ? "禁用" : "启用"}
                    </Button>
                ) : null}
                {canWrite ? (
                    <Button
                        danger
                        disabled={banButton}
                        type="link"
                        onClick={() => {
                            setDeleteTarget(row);
                            setDeleteSerial("");
                            setDeleteValidationError("");
                        }}
                    >
                        删除
                    </Button>
                ) : null}
            </Space>
        ),
        [banButton, canMonitor, canWrite, confirmOnline, confirmToggleState, handleMonitor]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 设备管理 ZhTable 的字段和操作入口。
    const deviceColumns = useMemo<TableColumnProps<DeviceVo>[]>(
        () => [
            {
                key: "name",
                label: "名称",
                minWidth: 148,
                prop: "device",
                render: ({ row }) => (
                    <Link to={`/logistics/device/deviceManagement/info/${row.device.id}`}>
                        {row.device.name || DEFAULT_VALUE}
                    </Link>
                )
            },
            {
                key: "type",
                label: "类型",
                width: 106,
                prop: "device",
                render: ({ row }) => formatEnum(DEVICE_TYPE, row.device.type)
            },
            {
                key: "ship",
                label: "关联船舶",
                minWidth: 176,
                prop: "ship",
                render: ({ row }) => row.ship?.cnname || row.ship?.name || DEFAULT_VALUE
            },
            {
                key: "serialNo",
                label: "序列号",
                minWidth: 148,
                prop: "device",
                render: ({ row }) => row.device.serialNo || DEFAULT_VALUE
            },
            {
                key: "online",
                label: "是否在线",
                width: 106,
                prop: "device",
                render: ({ row }) => (
                    <Tag color={row.device.online === ONLINE.ONLINE.code ? "green" : "default"}>
                        {formatEnum(ONLINE, row.device.online)}
                    </Tag>
                )
            },
            {
                key: "installDate",
                label: "安装日期",
                width: 154,
                prop: "device",
                render: ({ row }) => row.device.installDate || DEFAULT_VALUE
            },
            // 对齐旧 Vue 设备管理列表，补回设备推介人字段。
            {
                key: "referrer",
                label: "推介人",
                minWidth: 120,
                prop: "device",
                render: ({ row }) => row.device.referrer || DEFAULT_VALUE
            },
            {
                key: "installer",
                label: "安装人",
                minWidth: 120,
                prop: "device",
                render: ({ row }) => row.device.installer || DEFAULT_VALUE
            },
            {
                key: "user",
                label: "添加人",
                minWidth: 120,
                prop: "user",
                render: ({ row }) => row.user?.name || DEFAULT_VALUE
            },
            {
                key: "createdTime",
                label: "添加时间",
                width: 154,
                prop: "device.createdTime",
                sortable: true,
                render: ({ row }) => formatDate(row.device.createdTime)
            },
            {
                key: "state",
                label: "启用状态",
                width: 106,
                prop: "device",
                render: ({ row }) => formatEnum(ABLE, row.device.state)
            },
            {
                key: "operation",
                label: "操作",
                width: 365,
                prop: "operation",
                fixed: "right",
                render: ({ row }) => deviceOperation(row)
            }
        ],
        [deviceOperation]
    );

    return (
        <section aria-label="device-management-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space wrap>
                    <Input
                        aria-label="设备名称或序列号"
                        placeholder="请输入设备名或序列号搜索"
                        value={name}
                        onChange={event => setName(event.target.value)}
                    />
                    <Select
                        aria-label="是否在线"
                        placeholder="是否在线"
                        value={online}
                        onChange={setOnline}
                        options={[
                            { value: "", label: "是否在线" },
                            { value: "1", label: ONLINE.ONLINE.intro },
                            { value: "0", label: ONLINE.OFFLINE.intro }
                        ]}
                    />
                    <Select
                        aria-label="启用状态"
                        placeholder="启用状态"
                        value={state}
                        onChange={setState}
                        options={[
                            { value: "", label: "启用状态" },
                            { value: "1", label: ABLE.ENABLE.intro },
                            { value: "0", label: ABLE.DISABLE.intro }
                        ]}
                    />
                    <ZhDateRangePicker
                        format="YYYY-MM-DD"
                        rangeSeparator="-"
                        startPlaceholder="安装开始日期"
                        endPlaceholder="安装结束日期"
                        type="daterange"
                        value={installDateRange}
                        valueFormat="YYYY-MM-DD"
                        onChange={(_value, formattedValue) => setInstallDateRange(formattedValue || null)}
                    />
                    <Button type="primary" onClick={submitFilters}>
                        查询
                    </Button>
                    <Button onClick={resetFilters}>重置</Button>
                </Space>
            </Card>
            <Card>
                <ZhTable<DeviceVo>
                    rowKey={row => String(row.device.id)}
                    data={rows}
                    columns={deviceColumns}
                    emptyText="暂无设备"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="w-full" align="center">
                                <Typography.Title className="mb-0" level={4}>
                                    设备管理
                                </Typography.Title>
                                {canWrite ? (
                                    <Link to="/logistics/device/deviceManagement/add">
                                        <Button disabled={banButton} type="primary">
                                            新增设备
                                        </Button>
                                    </Link>
                                ) : null}
                                {canExport ? (
                                    <Button disabled={banButton} onClick={() => void handleExport()} type="primary">
                                        导出
                                    </Button>
                                ) : null}
                            </Space>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, pageSize) => {
                                    // 旧版 ProTable 切换条数后固定回到第 1 页，普通翻页则保留当前条数。
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }));
                                }}
                            />
                        </div>
                    }
                />
            </Card>
            <Modal
                open={pendingAction !== null}
                title="提示"
                okText="确认"
                cancelText="取消"
                confirmLoading={actionLoading}
                onOk={() => void handleActionConfirm()}
                onCancel={() => {
                    if (!actionLoading) setPendingAction(null);
                }}
            >
                {pendingAction?.type === "online"
                    ? "是否上线该设备？"
                    : pendingAction?.nextState === ABLE.ENABLE.code
                      ? "是否启用该设备？"
                      : "是否禁用该设备？"}
            </Modal>
            <Modal
                open={deleteTarget !== null}
                title="删除设备"
                okText="确认"
                cancelText="取消"
                confirmLoading={actionLoading}
                onOk={() => void handleDeleteConfirm()}
                onCancel={() => {
                    if (!actionLoading) setDeleteTarget(null);
                }}
            >
                <Input
                    aria-label="序列号校验"
                    maxLength={9}
                    placeholder="请输入序列号校验"
                    value={deleteSerial}
                    onChange={event => {
                        setDeleteSerial(event.target.value);
                        setDeleteValidationError("");
                    }}
                />
                {deleteValidationError ? (
                    <Typography.Text type="danger">{deleteValidationError}</Typography.Text>
                ) : null}
            </Modal>
            <ShipMonitorModal
                open={monitorOpen}
                device={monitorDevice}
                loadMonitorStream={loadMonitorStream}
                shipId={monitorShipId}
                ship={monitorShip}
                loadShipLocation={loadShipLocation}
                onCancel={() => setMonitorOpen(false)}
            />
        </section>
    );
}
